<template>
  <div>
    <!-- Upcoming trials (48h window, surfaced before follow-up buckets since trials are timed) -->
    <section v-if="upcomingTrials.length > 0">
      <header class="prospect-group-header">
        <span class="prospect-group-dot is-upcoming-trial"></span>
        <h4 class="prospect-group-title">Upcoming trials</h4>
        <span class="prospect-group-count">{{ upcomingTrials.length }}</span>
      </header>
      <div class="grid grid-cols-1 gap-2">
        <prospect-row
          v-for="family in upcomingTrials"
          :key="'trial-' + family.id"
          :family="family"
          :families-api-url="familiesApiUrl"
          :team-name="teamName"
          :current-author-id="currentAuthorId"
          :current-author-type="currentAuthorType"
          urgency="upcoming-trial"
          @open="$emit('open', $event)"
          @note-created="$emit('note-created', $event)"
          @stage-change="$emit('stage-change', $event)"
        />
      </div>
    </section>

    <!-- Overdue follow-up: flagged more than 3 days ago -->
    <section v-if="overdueFamilies.length > 0">
      <header class="prospect-group-header">
        <span class="prospect-group-dot is-overdue"></span>
        <h4 class="prospect-group-title">Overdue follow-up</h4>
        <span class="prospect-group-count">{{ overdueFamilies.length }}</span>
      </header>
      <div class="grid grid-cols-1 gap-2">
        <prospect-row
          v-for="family in overdueFamilies"
          :key="'overdue-' + family.id"
          :family="family"
          :families-api-url="familiesApiUrl"
          :team-name="teamName"
          :current-author-id="currentAuthorId"
          :current-author-type="currentAuthorType"
          urgency="overdue"
          @open="$emit('open', $event)"
          @note-created="$emit('note-created', $event)"
          @stage-change="$emit('stage-change', $event)"
        />
      </div>
    </section>

    <!-- Recent follow-up: flagged within the last 3 days -->
    <section v-if="recentFamilies.length > 0">
      <header class="prospect-group-header">
        <span class="prospect-group-dot is-today"></span>
        <h4 class="prospect-group-title">Follow up</h4>
        <span class="prospect-group-count">{{ recentFamilies.length }}</span>
      </header>
      <div class="grid grid-cols-1 gap-2">
        <prospect-row
          v-for="family in recentFamilies"
          :key="'recent-' + family.id"
          :family="family"
          :families-api-url="familiesApiUrl"
          :team-name="teamName"
          :current-author-id="currentAuthorId"
          :current-author-type="currentAuthorType"
          urgency="today"
          @open="$emit('open', $event)"
          @note-created="$emit('note-created', $event)"
          @stage-change="$emit('stage-change', $event)"
        />
      </div>
    </section>

    <!-- Empty state -->
    <div
      v-if="
        !loading &&
        overdueFamilies.length === 0 &&
        recentFamilies.length === 0 &&
        upcomingTrials.length === 0
      "
      class="text-center p-y-lg"
    >
      <template v-if="hiddenByFilters">
        <p class="text-16 text-gray-700 m-b-xs">
          <i class="fa-solid fa-filter text-yellow-600"></i> No leads match the current filters.
        </p>
        <p class="text-14 text-gray-500 m-b-sm">
          New leads may be hidden. Reset filters to see everything.
        </p>
        <button type="button" class="btn btn-default btn-sm" @click="$emit('reset-filters')">
          Reset filters
        </button>
      </template>
      <template v-else>
        <p class="text-16 text-gray-700 m-b-xs">
          <i class="fa-solid fa-circle-check text-green-500"></i> You're all caught up.
        </p>
        <p class="text-14 text-gray-500">
          No leads need your attention right now.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import ProspectRow from './prospect_row.vue';

export default {
  name: 'prospect-queue',
  components: { ProspectRow },
  props: {
    families: { type: Array, default: () => [] },
    upcomingTrials: { type: Array, default: () => [] },
    familiesApiUrl: { type: String, required: true },
    teamName: { type: String, default: '' },
    currentAuthorId: { type: Number, default: null },
    currentAuthorType: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    hiddenByFilters: { type: Boolean, default: false },
  },
  computed: {
    // Exclude families already surfaced as upcoming-trial from the follow-up buckets.
    trialFamilyIds() {
      const ids = new Set();
      this.upcomingTrials.forEach((f) => ids.add(f.id));
      return ids;
    },
    // Only families with at least one flagged child qualify for the queue.
    flaggedFamilies() {
      return this.families
        .filter((f) => f.needs_attention && !this.trialFamilyIds.has(f.id))
        .slice()
        .sort((a, b) => this.oldestFollowUpAt(a) - this.oldestFollowUpAt(b));
    },
    overdueFamilies() {
      return this.flaggedFamilies.filter((f) => this.worstFollowUpAge(f) > 3);
    },
    recentFamilies() {
      return this.flaggedFamilies.filter((f) => this.worstFollowUpAge(f) <= 3);
    },
  },
  methods: {
    // Oldest follow-up timestamp among the family's flagged children (ms epoch).
    // Families where the flag was set long ago sort ahead of fresh flags.
    oldestFollowUpAt(family) {
      const flagged = (family.prospects || [])
        .filter((c) => c.needs_follow_up && c.follow_up_set_at);
      if (flagged.length === 0) return Number.POSITIVE_INFINITY;
      return flagged.reduce((min, c) => {
        const t = new Date(c.follow_up_set_at).getTime();
        return t < min ? t : min;
      }, Number.POSITIVE_INFINITY);
    },
    worstFollowUpAge(family) {
      const oldest = this.oldestFollowUpAt(family);
      if (!Number.isFinite(oldest)) return 0;
      return Math.floor((Date.now() - oldest) / 86400000);
    },
  },
};
</script>
